import {
  AccountProcessType,
  CheckoutEntry,
  CheckoutSopMetadata,
  getCountrySpecificMasterConfig,
  getValueCheck,
} from '@commerce-checkout-webcomponents/sdk';
import { formatPrice, formatNumber } from '@nextgen-web-framework/core';
import { Cart, SubCartDetail, VariantDimensions } from '@commerce-cart-webcomponents/sdk';
import { IconStyle, TagStyle, Theme, TagTheme } from '@nextgen-web-framework/all';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { IProductEntries, ProductCardTagProps } from '../checkout-order-summary/types/propTypes';
import { DataTypesEnums } from './config/checkoutConstants';
import { ICheckoutOrderEntries, valueMappingObject } from './types/CheckoutType';

const enableRegRenProductPrice = () => getCountrySpecificMasterConfig()?.UiOrderSummaryKitProductPriceVisible;

export const fetchFeatureFlags = async () => {
  try {
    const tuples = ApiConfigService.getTuples();

    const featureRequestUrl = `${ConfigService.getConfigs().NEXT_PUBLIC_WEB_BASE_PATH}/feature-flags/v2`;

    const featureRequestOptions = {
      method: 'POST',
      headers: {
        ...tuples,
      },
      body: JSON.stringify({
        profiles: ['webshop'],
      }),
    };
    // logger.log('The data for the fetch request', featureRequestUrl, featureRequestOptions);
    const res = await fetch(featureRequestUrl, featureRequestOptions);
    return await res.json();
  } catch (error) {
    // logger.error('feature flag fetch failed', error);
    return {};
  }
};

export const checkoutFeatureFlagConfig = () => {
  const cartselectionFlag = Boolean(FeatureFlagService.getFeatureVisibility('purchase.cartselection'));
  const enableServiceLocationSearchEnhancement = Boolean(
    FeatureFlagService.getFeatureVisibility('webshop.serviceLocationSearchEnhancementEnable'),
  );
  const showPickupContactFieldInCheckout = Boolean(
    FeatureFlagService.getFeatureVisibility('checkout.pickup.contact.in.checkout.enable'),
  );
  const paymentRedirectionEnabled = Boolean(FeatureFlagService.getFeatureVisibility('payment.redirection.enable'));

  return {
    cartselectionFlag,
    enableServiceLocationSearchEnhancement,
    showPickupContactFieldInCheckout,
    paymentRedirectionEnabled,
  };
};

export const getFormatedPrice = (value?: number, isRequired?: boolean) => {
  if (!value) {
    return isRequired ? `${formatPrice(0)}` : '';
  }
  return `${formatPrice(value) || ''}`;
};

export const getFormatedNumber = (value?: number, isRequired?: boolean) => {
  if (!value) {
    return isRequired ? `${formatNumber(0)}` : '';
  }
  return `${formatNumber(value)}`;
};

export const getKeymappingForGroupOrderFields = (key: string, isParentCheckout: boolean): string => {
  const valueMappingObj: valueMappingObject = {
    subTotal: {
      true: 'groupSubTotal',
      false: 'subTotal',
    },
    discount: {
      true: 'groupDiscount',
      false: 'discount',
    },
    totalItemLevelDiscount: {
      true: 'groupTotalItemLevelDiscount',
      false: 'totalItemLevelDiscount',
    },
    totalOrderLevelDiscount: {
      true: 'groupTotalOrderLevelDiscount',
      false: 'totalOrderLevelDiscount',
    },
    totalPriceForExternalPayment: {
      true: 'groupTotalPriceForExternalPayment',
      false: 'totalPriceForExternalPayment',
    },
    totalTax: {
      true: 'groupTotalTax',
      false: 'totalTax',
    },
    totalPrice: {
      true: 'groupTotalPrice',
      false: 'totalPrice',
    },
    totalAdjustedPvBv: {
      true: 'groupTotalAdjustedPvBv',
      false: 'totalAdjustedPvBv',
    },
    totalPvBvAfterAdjustment: {
      true: 'groupTotalPvBvAfterAdjustment',
      false: 'totalPvBvAfterAdjustment',
    },
    totalPromotionDiscount: {
      true: 'groupTotalPromotionDiscount',
      false: 'totalPromotionDiscount',
    },
    totalCouponDiscount: {
      true: 'groupTotalCouponDiscount',
      false: 'totalCouponDiscount',
    },
    totalDiscount: {
      true: 'groupTotalDiscount',
      false: 'totalDiscount',
    },
  };
  return valueMappingObj[key][isParentCheckout.toString()];
};

export const isObjectEmpty = (obj?: unknown) => {
  let objEmpty = false;
  if (!obj || Object.keys(obj).length == 0) {
    objEmpty = true;
  }
  return objEmpty;
};

export const undefinedCheckHandler = (value?: number | string | undefined, shouldNotBeEmpty?: boolean) => {
  if (!shouldNotBeEmpty || value == undefined) {
    return '';
  }
  return `${value}`;
};

export const getTagBasedOnCheck = (
  tagCheck: boolean | undefined,
  tag: ProductCardTagProps,
  defaultTag?: ProductCardTagProps,
) => {
  if (tagCheck) {
    return [tag];
  }
  return defaultTag ? [defaultTag] : [];
};

export const isRegFlowFromCart = (cart: Cart) =>
  cart?.accountProcess?.type && cart?.accountProcess?.type == AccountProcessType.Registration;

const isRegOrRenewProductCheck = (entry: CheckoutEntry) =>
  entry.product.flags?.isRegProduct ||
  entry.product.flags?.isRenewalProduct ||
  entry.product.flags?.isUpgradeProduct ||
  entry.product.flags?.isLateRenewalProduct ||
  entry.product.flags?.isDowngradeProduct;

const isFeeItem = (entry: CheckoutEntry) => isRegOrRenewProductCheck(entry) && entry.product.flags?.isFeeItem;

export const strikeThroughPriceValue = (strikeAmount?: number, totalAmount?: number) =>
  getValueCheck(strikeAmount, DataTypesEnums.TYPE_NUMBER) == getValueCheck(totalAmount, DataTypesEnums.TYPE_NUMBER) ||
  getValueCheck(strikeAmount, DataTypesEnums.TYPE_NUMBER) == 0
    ? undefined
    : getValueCheck(strikeAmount, DataTypesEnums.TYPE_NUMBER);

const productQuantity = (entry: CheckoutEntry, isBackOrder?: boolean) =>
  !isBackOrder ? entry.quantity : getValueCheck(entry?.backOrderQuantity, DataTypesEnums.TYPE_NUMBER);

export const getProductEntriesByEntries = (
  entries: Array<CheckoutEntry>,
  totalItems: number,
  currencyIsoCode: string,
  translation: Function,
  filterBackOrderEntries?: boolean,
  isProxyGcoOrder?: boolean,
): ICheckoutOrderEntries => {
  const productEntries: Array<IProductEntries> = [];
  let hasEkitProduct = false;
  let totalItemCount = totalItems;
  entries?.map((entry: CheckoutEntry) => {
    const isRegRenProduct = isRegOrRenewProductCheck(entry);
    if (!entry?.sopElementId && !isFeeItem(entry)) {
      let prodName = '';
      let varient = '';
      totalItemCount += entry.quantity;
      prodName = entry.product?.variantDimensions?.length
        ? getValueCheck(
            `${entry.product.name} - ${entry.product.variantDimensions[0].value}`,
            DataTypesEnums.TYPE_STRING,
          )
        : entry.product.name;
      const variantDimensionsLength = getValueCheck(
        entry.product.variantDimensions?.length,
        DataTypesEnums.TYPE_NUMBER,
      );
      variantDimensionsLength > 1 &&
        entry.product.variantDimensions?.map((variantDimension: VariantDimensions, index: number) => {
          index && (varient += `${variantDimension.value} `);
        });

      const productEntryObj: IProductEntries = !isRegRenProduct
        ? {
            brandName: getValueCheck(entry.product?.brand, DataTypesEnums.TYPE_STRING),
            productName: prodName,
            productId: getValueCheck(entry.product?.id, DataTypesEnums.TYPE_STRING),
            varient: varient,
            currency: getValueCheck(currencyIsoCode, DataTypesEnums.TYPE_STRING),
            productPrice: getValueCheck(entry.totalPrice?.value, DataTypesEnums.TYPE_NUMBER),
            link: getValueCheck(entry?.product?.imageUrl, DataTypesEnums.TYPE_STRING),
            alt: getValueCheck(entry?.product?.name, DataTypesEnums.TYPE_STRING),
            productQuantity: productQuantity(entry, filterBackOrderEntries),
            pointValue: undefinedCheckHandler(entry.totalPrice?.amwayValue?.pointValue, true),
            businessVolume: undefinedCheckHandler(entry.totalPrice?.amwayValue?.businessVolume, true),
            strikethroughPrice: strikeThroughPriceValue(entry.entrySubTotal?.value, entry.totalPrice?.value),
            backOrderEta: getValueCheck(entry?.backOrderEta, DataTypesEnums.TYPE_STRING),
            itemStatus: getValueCheck(entry?.itemStatus, DataTypesEnums.TYPE_STRING),
            backOrderQuantity: getValueCheck(entry?.backOrderQuantity, DataTypesEnums.TYPE_NUMBER),
            availabilityStatus: getValueCheck(entry?.restrictionDetail, DataTypesEnums.TYPE_STRING),
            productSize: getValueCheck(entry?.product?.servingSize, DataTypesEnums.TYPE_STRING),
            tags: [
              ...getTagBasedOnCheck(entry?.enablePromotionTag, {
                icon: {
                  iconName: 'tags' as IconName,
                  iconStyle: IconStyle.REGULAR,
                },
                text: '',
                style: TagStyle.SOLID,
                theme: Theme.SUCCESS as TagTheme,
              }),
              ...getTagBasedOnCheck(entry?.product?.flags?.isInstallmentPlanEligible && !isProxyGcoOrder, {
                icon: {
                  iconName: 'calendar-check' as IconName,
                  iconStyle: IconStyle.REGULAR,
                },
                text: translation('checkout.CheckoutProductItems.installmentText'),
                style: TagStyle.OUTLINE,
                theme: Theme.BLUE as TagTheme,
              }),
            ],
          }
        : {
            brandName: getValueCheck(entry.product?.brand, DataTypesEnums.TYPE_STRING),
            productName: prodName,
            productId: getValueCheck(entry.product?.id, DataTypesEnums.TYPE_STRING),
            link: getValueCheck(entry?.product?.imageUrl, DataTypesEnums.TYPE_STRING),
            alt: getValueCheck(entry?.product?.name, DataTypesEnums.TYPE_STRING),
            productSize: getValueCheck(entry?.product?.itemDescription, DataTypesEnums.TYPE_STRING),
            ...(enableRegRenProductPrice()
              ? { productPrice: getValueCheck(entry.totalPrice?.value, DataTypesEnums.TYPE_NUMBER) }
              : {}),
          };
      !filterBackOrderEntries && productEntries.push(productEntryObj);
      filterBackOrderEntries && productEntryObj.backOrderQuantity && productEntries.push(productEntryObj);
      isRegRenProduct && (hasEkitProduct = true);
    }
  });
  return { productEntries, totalItems: totalItemCount, hasEkitProduct };
};

export const getProductOrderInfoText = (translation: Function, elementSequence?: number, totalShipments?: number) => {
  if (!elementSequence) {
    return undefined;
  } else if (elementSequence && (!totalShipments || totalShipments > 998)) {
    return translation('checkout.CheckoutProductItems.orderInfoNumberOnly', {
      elementSequence: elementSequence,
    });
  } else {
    return translation('checkout.CheckoutProductItems.orderInfoText', {
      elementSequence: elementSequence,
      totalShipments: totalShipments,
    });
  }
};

export const sopPlanProduct = (checkoutEntries: CheckoutEntry[], sopEntryId?: number) => {
  const checkoutEntryOfSopEntryId = checkoutEntries?.find((entry) => entry?.entryId == sopEntryId);
  return checkoutEntryOfSopEntryId?.product;
};

export const getSopEntries = (
  entries: Array<CheckoutSopMetadata>,
  totalItems: number,
  currencyIsoCode: string,
  translation: Function,
  filterBackOrderEntries = false,
  checkoutEntries: CheckoutEntry[],
): ICheckoutOrderEntries => {
  const productEntries: Array<IProductEntries> = [];
  let totalItemsCount = totalItems;
  entries?.map((entry: CheckoutSopMetadata) => {
    totalItemsCount += getValueCheck(entry.quantity, DataTypesEnums.TYPE_NUMBER);
    const productEntryObj: IProductEntries = {
      brandName: '',
      productName:
        (entry?.entryIds?.length == 1 && sopPlanProduct(checkoutEntries, entry?.entryIds[0])?.name) ||
        getValueCheck(entry?.planName, DataTypesEnums.TYPE_STRING),
      productId: getValueCheck(entry?.sopId, DataTypesEnums.TYPE_STRING),
      varient: '',
      productSize: entry?.sopCode
        ? translation('checkout.CheckoutProductItems.subscriptionId', { sopCode: entry.sopCode })
        : '',
      currency: getValueCheck(currencyIsoCode, DataTypesEnums.TYPE_STRING),
      productPrice: getValueCheck(entry.totalPrice?.value, DataTypesEnums.TYPE_NUMBER),
      link:
        (entry?.entryIds?.length == 1 && sopPlanProduct(checkoutEntries, entry?.entryIds[0])?.imageUrl) ||
        getValueCheck(entry?.images?.[0]?.imageUrl, DataTypesEnums.TYPE_STRING),
      alt: getValueCheck(entry?.planName, DataTypesEnums.TYPE_STRING),
      pointValue: undefinedCheckHandler(entry.totalPrice?.amwayValue?.pointValue, true),
      businessVolume: undefinedCheckHandler(entry.totalPrice?.amwayValue?.businessVolume, true),
      strikethroughPrice: strikeThroughPriceValue(entry.subTotal?.value, entry.totalPrice?.value),
      backOrderEta: getValueCheck(entry?.backOrderEta, DataTypesEnums.TYPE_STRING),
      itemStatus: getValueCheck(entry?.itemStatus, DataTypesEnums.TYPE_STRING),
      productQuantity: entry?.quantity,
      tags: [
        ...getTagBasedOnCheck(entry?.isPromotionApplied, {
          icon: {
            iconName: 'tags' as IconName,
            iconStyle: IconStyle.REGULAR,
          },
          text: '',
          style: TagStyle.SOLID,
          theme: Theme.SUCCESS as TagTheme,
        }),
        {
          icon: {
            iconName: 'refresh' as IconName,
            iconStyle: IconStyle.REGULAR,
          },
          text: `${translation('checkout.CheckoutProductItems.sopText')}: ${getProductOrderInfoText(
            translation,
            entry?.elementSequence,
            entry?.totalShipments,
          )}`,
          style: TagStyle.SOLID,
          theme: Theme.PURPLE as TagTheme,
        },
        ...getTagBasedOnCheck(entry?.isCompensated, {
          icon: {
            iconName: 'bolt' as IconName,
            iconStyle: IconStyle.REGULAR,
          },
          text: translation('checkout.CheckoutProductItems.compensantedText'),
          style: TagStyle.OUTLINE,
          theme: Theme.PURPLE as TagTheme,
        }),
      ],
    };
    if (!filterBackOrderEntries) {
      productEntries.push(productEntryObj);
    }
    filterBackOrderEntries && entry.hasBackOrderableItems && productEntries.push(productEntryObj);
  });
  return { productEntries, totalItems: totalItemsCount };
};

export const findMainCart = (subCarts: Array<SubCartDetail>, cartId: string) =>
  subCarts.find((subCart) => subCart.subCartId == cartId);

const emailRegexDefault = /^[\w.-]{1,64}@(?!.{255})[\w.-]+\.[A-Za-z]{2,4}$/;

export const isValidEmail = (email: string | undefined, emailRegex: RegExp = emailRegexDefault) =>
  email == '' || email == undefined ? false : emailRegex.test(String(email).toLowerCase());

export const stateTextLocalization = (value: string | undefined, tranlation: Function, tranlationLabel: string) => {
  if (value) {
    return tranlation(`${tranlationLabel}${value}`);
  }
  return '';
};

export const stringBooleanUndefinedChecksOutput = (
  checks: Array<string | boolean | undefined>,
  isOptionalCheck?: boolean,
) => {
  let checkResult: string | boolean | undefined = undefined;
  if (isOptionalCheck) {
    checkResult = false;
    checks.forEach((check: string | boolean | undefined) => {
      if (check) {
        checkResult = true;
      }
    });
  } else {
    checkResult = true;
    checks.forEach((check: string | boolean | undefined) => {
      if (!check) {
        checkResult = false;
      }
    });
  }
  return checkResult;
};

export const returnValueBasedOnCheck = (
  check: object | string | boolean | undefined,
  value: object | string | boolean | undefined,
  fallbackValue: object | string | boolean | undefined,
) => {
  if (check) {
    return value;
  }
  return fallbackValue;
};

export const validateAll = (...conditions: unknown[]): boolean => conditions.every(Boolean);
